import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import './ApplyPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ApplyPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    ownerName: '',
    businessName: '',
    phone: '',
    email: '',
    gstNumber: '',
    district: '',
    investmentCapacity: '5-10 Lakhs',
    experience: ''
  });

  const [loading, setLoading] = useState(false);

  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
  const API_URL = envApiUrl && (isLocalHost || (!envApiUrl.includes("localhost") && !envApiUrl.includes("127.0.0.1"))) ? envApiUrl : '/api';

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/dealer-applications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const json = await response.json();

      if (response.ok && json.success) {
        alert(t('apply_page.success_msg'));
        setForm({
          ownerName: '',
          businessName: '',
          phone: '',
          email: '',
          gstNumber: '',
          district: '',
          investmentCapacity: '5-10 Lakhs',
          experience: ''
        });
      } else {
        alert(json.message || t('apply_page.fail_msg'));
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      alert(t('apply_page.error_msg'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="apply-page">
      <Header />
      <div className="apply-top-bar"></div>

      <section className="apply-hero">
        <div className="container" style={{ position: 'relative' }}>
          <button className="contact-back-btn" onClick={() => navigate('/dealer-locator')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ verticalAlign: 'middle' }}>
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            <span className="contact-back-btn-text">{t('apply_page.back_to_dealer', 'Back to Dealer Locator')}</span>
          </button>
          <span className="apply-badge">{t('apply_page.badge')}</span>
          <h1 className="apply-title">{t('apply_page.title')}</h1>
          <p className="apply-subtitle">{t('apply_page.subtitle')}</p>
        </div>
      </section>

      <section className="apply-main">
        <div className="container">
          <div className="apply-grid">
            
            {/* Left Column — Partnership Info */}
            <div className="apply-info-card">
              <h2 className="card-heading">{t('apply_page.why_partner')}</h2>
              <p className="partnership-intro-text">
                {t('apply_page.intro_text')}
              </p>

              <div className="info-row">
                <div className="info-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
                    <path d="M2 17l10 5 10-5"></path>
                  </svg>
                </div>
                <div>
                  <strong>{t('apply_page.exclusive_rights_title')}</strong>
                  <p>{t('apply_page.exclusive_rights_desc')}</p>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="1" x2="12" y2="23"></line>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                  </svg>
                </div>
                <div>
                  <strong>{t('apply_page.high_margin_title')}</strong>
                  <p>{t('apply_page.high_margin_desc')}</p>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 2 7 12 12 22 7 12 2"></polygon>
                    <polyline points="2 17 12 22 22 17"></polyline>
                    <polyline points="2 12 12 17 22 12"></polyline>
                  </svg>
                </div>
                <div>
                  <strong>{t('apply_page.marketing_support_title')}</strong>
                  <p>{t('apply_page.marketing_support_desc')}</p>
                </div>
              </div>

              <div className="info-row">
                <div className="info-icon green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div>
                  <strong>{t('apply_page.subsidy_approved_title')}</strong>
                  <p>{t('apply_page.subsidy_approved_desc')}</p>
                </div>
              </div>
            </div>

            {/* Right Column — Dealer Application Form */}
            <div className="apply-form-card">
              <h2 className="card-heading">{t('apply_page.submit_form')}</h2>
              <form onSubmit={handleSubmit} className="apply-form">
                
                <div className="form-row-double">
                  <div>
                    <label className="form-label">{t('apply_page.owner_name')} <span className="req">*</span></label>
                    <input
                      type="text"
                      name="ownerName"
                      placeholder={t('apply_page.owner_ph')}
                      value={form.ownerName}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">{t('apply_page.business_name')} <span className="req">*</span></label>
                    <input
                      type="text"
                      name="businessName"
                      placeholder={t('apply_page.business_ph')}
                      value={form.businessName}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row-double">
                  <div>
                    <label className="form-label">{t('apply_page.contact_phone')} <span className="req">*</span></label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder={t('apply_page.phone_ph')}
                      value={form.phone}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      title="Please enter a valid 10-digit mobile number"
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">{t('apply_page.email_address')} <span className="req">*</span></label>
                    <input
                      type="email"
                      name="email"
                      placeholder={t('apply_page.email_ph')}
                      value={form.email}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row-double">
                  <div>
                    <label className="form-label">{t('apply_page.gst_number')} <span className="req">*</span></label>
                    <input
                      type="text"
                      name="gstNumber"
                      placeholder={t('apply_page.gst_ph')}
                      value={form.gstNumber}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                  <div>
                    <label className="form-label">{t('apply_page.district')} <span className="req">*</span></label>
                    <input
                      type="text"
                      name="district"
                      placeholder={t('apply_page.district_ph')}
                      value={form.district}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>
                </div>

                <label className="form-label">{t('apply_page.investment_cap')} <span className="req">*</span></label>
                <select
                  name="investmentCapacity"
                  value={form.investmentCapacity}
                  onChange={handleChange}
                  required
                  className="form-select"
                >
                  <option value="Under 2 Lakhs">Under 2 Lakhs</option>
                  <option value="2-5 Lakhs">2 - 5 Lakhs</option>
                  <option value="5-10 Lakhs">5 - 10 Lakhs</option>
                  <option value="10-20 Lakhs">10 - 20 Lakhs</option>
                  <option value="20+ Lakhs">20 Lakhs +</option>
                </select>

                <label className="form-label">{t('apply_page.experience')} <span className="req">*</span></label>
                <textarea
                  name="experience"
                  placeholder={t('apply_page.experience_ph')}
                  value={form.experience}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="form-textarea"
                ></textarea>

                <button type="submit" className="btn-submit-apply" disabled={loading}>
                  {loading ? (
                    <span className="btn-spinner-text">
                      <span className="spinner"></span> {t('apply_page.btn_submitting')}
                    </span>
                  ) : (
                    t('apply_page.btn_submit')
                  )}
                </button>
              </form>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ApplyPage;


